import Mustache from "mustache"
import debounce from "lodash/debounce"
import { addEventListenerWithKey } from "shared/lib/utils.js"
import { onBFCacheLoad } from "shared/scripts/back_forward_cache_utils"
import { fetchAllServices, Service } from "./recommended_services"
import { RecentSearches } from "./recent_searches"
import { recommendationTemplate } from "./templates"
import { saveUserSearch, saveSearchTermBypassLength, createSearchItemFromElement, SearchEntryInterface } from "./search_history"
import { SearchHelperNavigation } from "./search_helper_navigation"

export const UniversalSearchHeader = () => {
  const universalSearch = document.querySelector("#universal-search")
  if (!universalSearch) return

  // unhide the dropdown, hidden by default to avoid FOUC
  // unhide the search input, hidden by default on mobile
  universalSearch.parentElement?.removeAttribute("style")
  universalSearch.querySelector(".universal-search-helper")?.removeAttribute("style")

  const inputSearch = universalSearch.querySelector("input")
  const searchResultsContainer = universalSearch.querySelector(".search-preview > ul")

  RecentSearches()

  // Fetched on first interaction, not on page load: this feeds the fuzzy index
  // alone now that the tiles are server-rendered. runWhenIdle was not enough --
  // its 2s timeout still fired for crawlers, which then abandoned the request on
  // navigation. Bots do not interact with inputs.
  const MAX_SERVICE_FETCH_ATTEMPTS = 3

  let allServices: Service[] = []
  let servicesPromise: Promise<void> | null = null
  let fusePromise: Promise<{ search: (term: string) => Array<{ item: unknown }> }> | null = null
  let serviceFetchAttempts = 0
  // The memo is cleared so the next interaction retries; the cap stops a hard-down
  // endpoint being hit once per focus for as long as the page is open.
  const resetServices = () => {
    if (serviceFetchAttempts >= MAX_SERVICE_FETCH_ATTEMPTS) return
    servicesPromise = null
    fusePromise = null
  }
  const ensureServices = () => {
    if (!servicesPromise) {
      serviceFetchAttempts += 1
      // Settle on both outcomes: if this stayed pending, getFuse() and every
      // queued keystroke would hang forever.
      servicesPromise = fetchAllServices().then(
        services => {
          allServices = services
          // fetchAllServices swallows its errors into [], so empty is
          // indistinguishable from failed -- and neither is a legitimate result.
          if (services.length === 0) resetServices()
        },
        () => resetServices(),
      )
    }
    return servicesPromise
  }

  SearchHelperNavigation(universalSearch)

  // Lazy-load Fuse.js (~12KB off the initial bundle), downloading the chunk in
  // parallel with the service fetch so neither waits on the other.
  const getFuse = () => {
    if (!fusePromise) {
      fusePromise = Promise.all([ensureServices(), import("fuse.js")]).then(
        ([, { default: Fuse }]) => new Fuse(allServices, { includeScore: true, threshold: 0.3, keys: ["name"] }),
      )
    }
    return fusePromise
  }

  const triggerSearch = async (event: Event) => {
    const keyboardEvent = event as KeyboardEvent
    const searchTerm = (keyboardEvent.target as HTMLInputElement).value.trim()

    if (!searchTerm) return

    if (keyboardEvent.key === "Enter") {
      window.App.Utils.logCustomDatadogAction("universal_search_trigger_via_enter", { term: searchTerm })

      // No service name or module_name contains a space, so a multi-word term
      // takes the query branch regardless -- don't stall it on the fetch. A
      // single word waits, since reading allServices mid-flight would downgrade
      // an exact match to a generic query search.
      if (!/\s/.test(searchTerm)) {
        await ensureServices()
      }

      const serviceMatch = allServices.find(
        service =>
          service.name.toLowerCase() === searchTerm.toLowerCase() ||
          service.module_name.toLowerCase() === searchTerm.toLowerCase()
      )

      if (serviceMatch) {
        // Exact match with a service name, navigate to the service page
        saveUserSearch({
          type: "service",
          term: searchTerm,
          details: {
            name: serviceMatch.name,
            brand_color: serviceMatch.brand_color,
            module_name: serviceMatch.module_name,
            image: serviceMatch.image,
          }
        })
        window.location.assign(`/${serviceMatch.module_name}`)
      } else {
        saveUserSearch({ type: "search", term: searchTerm })
        window.location.assign(`/search/query/${encodeURIComponent(searchTerm.toLowerCase())}`)
      }
    }
  }

  const handleSearchInput = (searchResultsContainer: Element) => async (event: Event) => {
    const searchTerm = (event.target as HTMLInputElement).value.trim()

    if (!searchTerm) return

    const fuse = await getFuse()
    const results = fuse.search(searchTerm).slice(0, 12)

    if (searchResultsContainer) {
      searchResultsContainer.innerHTML = ""
      results.forEach(result => {
        const rendered = Mustache.render(recommendationTemplate, result.item)
        searchResultsContainer.insertAdjacentHTML("beforeend", rendered)
      })
    }
  }

  const recommendedServiceClick = (event: Event) => {
    const serviceAnchor = (event.target as HTMLElement).closest("li > a") as HTMLAnchorElement | null

    const searchEntry = createSearchItemFromElement(serviceAnchor) as SearchEntryInterface | null
    if (!searchEntry) return
    // Bypasses MINIMUM_SEARCH_TERM_LENGTH: this is a click on a known service,
    // not a typed query, and short names (MyQ) would otherwise never be recorded.
    saveSearchTermBypassLength(searchEntry)
    if (inputSearch.value.trim()) {
      // Click on filtered recommended services
      window.App.Utils.logCustomDatadogAction(`universal_search_service_filter_clicked`, {
        service: serviceAnchor?.dataset?.term,
      })
    } else {
      // On explore, clicking on recent search that is a service, the result is a service search (same from recommended service).
      // On header, even on recent search, the service click will navigate to the service page.
      if (searchEntry.type === "search") {
        window.App.Utils.logCustomDatadogAction(`universal_search_recent_searches_clicked`, {
          term: serviceAnchor?.dataset?.term,
        })
      } else {
        window.App.Utils.logCustomDatadogAction(`universal_search_recommended_service_clicked`, {
          service: serviceAnchor?.dataset?.term,
        })
      }
    }
  }

  const debouncedHandleInput = debounce(handleSearchInput(searchResultsContainer), 100)
  addEventListenerWithKey(inputSearch, "input", "universal_search_header_input", debouncedHandleInput)
  addEventListenerWithKey(inputSearch, "keydown", "universal_search_header_keydown", triggerSearch)

  const recentSearchesWrapper = universalSearch.querySelector(".recent-searches")
  if (recentSearchesWrapper) {
    addEventListenerWithKey(
      recentSearchesWrapper,
      "click",
      "universal_search_header_recent_click",
      recommendedServiceClick
    )
  }

  const recommendedServicesWrapper = universalSearch.querySelector(".recommended-services")
  if (recommendedServicesWrapper) {
    addEventListenerWithKey(
      recommendedServicesWrapper,
      "click",
      "universal_search_header_recommended_click",
      recommendedServiceClick
    )
  }

  const filteredServicesWrapper = universalSearch.querySelector(".search-preview")
  if (filteredServicesWrapper) {
    addEventListenerWithKey(
      filteredServicesWrapper,
      "click",
      "universal_search_header_filtered_click",
      recommendedServiceClick
    )
  }

  const searchNavigation = universalSearch.querySelector("a.search-navigation")
  if (searchNavigation) {
    addEventListenerWithKey(searchNavigation, "click", "universal_search_header_navigation_click", () => {
      window.App.Utils.logCustomDatadogAction("universal_search_trigger_via_navigation_link", {
        term: inputSearch?.value.trim(),
      })
    })
  }

  // Fires before focus for mouse users, and never for crawlers.
  addEventListenerWithKey(universalSearch, "pointerenter", "universal_search_header_pointerenter", () => {
    void getFuse()
  })

  addEventListenerWithKey(inputSearch, "focus", "universal_search_header_focus", () => {
    // Warms the list and the Fuse chunk, keeping first-keystroke latency close to
    // the old page-load prefetch this replaces (review: cjanik).
    void getFuse()
    window.App.Utils.logCustomDatadogAction("universal_search_click_search_bar")
  })

  const mobileIconSearch = document.querySelector("span.mobile-icon-search")
  if (mobileIconSearch) {
    addEventListenerWithKey(mobileIconSearch, "click", "universal_search_header_mobile_icon_click", () => {
      document.body.classList.add("mobile-universal-search-enabled")
      inputSearch?.focus()
      window.App.Utils.logCustomDatadogAction("mobile_universal_search_view")
    })
  }

  const mobileIconClose = document.querySelector(".mobile-close-search-icon")
  if (mobileIconClose) {
    addEventListenerWithKey(mobileIconClose, "click", "universal_search_header_mobile_close_click", () => {
      document.body.classList.remove("mobile-universal-search-enabled")
      inputSearch?.blur()
    })
  }

  addEventListenerWithKey(window, "scroll", "universal_search_header_scroll", () => {
    if (!document.body.classList.contains("mobile-universal-search-enabled")) return
    inputSearch?.blur()
  })

  onBFCacheLoad("universal_search_header", () => {
    if (inputSearch) {
      inputSearch.value = ""
      inputSearch.dispatchEvent(new Event("input", { bubbles: true }))
    }
    if (searchResultsContainer) searchResultsContainer.innerHTML = ""

    UniversalSearchHeader()
  })
}
